// Chibi rig — 48×56 logical cell. Huge head, stubby body + legs. Three
// body types: Virgin (fat/slouched/baggy), Chad (fit/V-shape/upright),
// Civilian (neutral rectangle). Body-type silhouette is the primary
// faction differentiator; color comes second.
//
// Directions: 0=DOWN 1=LEFT 2=RIGHT 3=UP
// States: idle (2f) / walk (8f) / attack (6f) / hurt (2f) / death (3f)

export const CELL_W = 48;
export const CELL_H = 56;

// Shared joint anchors. Per-body-type widths live in BODY below.
export const BASE = {
  // Head: 20 wide × 20 tall, centered, dominant 40% of vertical space
  headCx: 24,
  headTop: 4,
  headBottom: 22,
  headHalfW: 10,
  headHalfH: 9,

  // Torso: y 22 → 40 (18 tall)
  torsoTop: 22,
  torsoBottom: 40,

  // Hips + legs: y 40 → 52
  hipY: 40,
  hipL_x: 20,
  hipR_x: 27,
  legBottom: 52,

  // Feet: y 52 → 55
  footY: 52,
  footBottom: 55,

  // Weapon anchor (forward hand at rest)
  weaponX: 33,
  weaponY: 28,
} as const;

// Body-type-specific geometry. Widths + shapes drive silhouette.
export const BODY = {
  virgin: {
    // Rounded pear silhouette — narrow shoulders, wider belly
    shoulderHalf: 8,
    chestHalf: 9,
    bellyHalf: 11,     // WIDEST at belly — belly-forward soft body
    hipHalf: 10,
    // Legs slightly bowed out
    legHalf: 2.5,
    // Arms hang short, sleeves baggy
    armLen: 10,
    armHalf: 2,
    // Head slouch — tilts forward
    headSlouchY: 1,
    // Belly extends forward below torso top
    bellyTop: 28,
  },
  chad: {
    // Inverted-V — broad shoulders, narrow waist
    shoulderHalf: 12,
    chestHalf: 11,
    bellyHalf: 8,       // narrow waist
    hipHalf: 9,
    legHalf: 3,
    armLen: 11,
    armHalf: 3,         // thicker arms (biceps)
    headSlouchY: -1,    // head held high
    bellyTop: 30,
  },
  civilian: {
    shoulderHalf: 9,
    chestHalf: 9,
    bellyHalf: 9,       // rectangular
    hipHalf: 9,
    legHalf: 2.5,
    armLen: 10,
    armHalf: 2,
    headSlouchY: 0,
    bellyTop: 28,
  },
} as const;

export type BodyType = keyof typeof BODY;

// ---- State meta ----
export const STATE_META = {
  idle:   { rowOffset:  0, frameCount: 2, fps:  2.5 },
  walk:   { rowOffset:  4, frameCount: 8, fps: 12 },
  attack: { rowOffset:  8, frameCount: 6, fps: 18 },
  hurt:   { rowOffset: 12, frameCount: 2, fps: 10 },
  death:  { rowOffset: 16, frameCount: 3, fps:  6 },
} as const;

export const SHEET_COLS = 8;
export const SHEET_ROWS = 20; // 4 dirs × 5 states

// ---- Pose curves ----
// Each body type has its own flavor of movement.

// IDLE (2 frames) — subtle breathing bob
export const IDLE_VIRGIN = {
  torsoDy: [0, 1],   // virgin has heavier bob (slouchy)
  headDy:  [0, 1],
  armDy:   [0, 1],
};
export const IDLE_CHAD = {
  torsoDy: [0, -1],  // subtle chest expansion upward
  headDy:  [0, -1],
  armDy:   [0, 0],
};
export const IDLE_CIVILIAN = {
  torsoDy: [0, 0],
  headDy:  [0, -1],
  armDy:   [0, 0],
};

// WALK (8 frames) — virgin shuffles, chad struts, civilian walks
export const WALK_VIRGIN = {
  // Heavy bob, minimal arm swing, short steps
  torsoDy:   [0,  1,  2,  1, 0,  1,  2,  1],
  headDy:    [0,  1,  1,  1, 0,  1,  1,  1],
  armFwdDx:  [0,  0,  1,  0, 0,  0, -1,  0],  // sluggish
  armFwdDy:  [0,  1,  0,  0, 0,  1,  0,  0],
  armBackDx: [0,  0, -1,  0, 0,  0,  1,  0],
  armBackDy: [0,  1,  0,  0, 0,  1,  0,  0],
  legLDx:    [0, -1, -1, 0, 0,  0,  0, 0],  // shuffle not stride
  legLDy:    [0,  0,  0, 0, 0,  0,  0, 0],
  legRDx:    [0,  0,  0, 0, 0,  1,  1, 0],
  legRDy:    [0,  0,  0, 0, 0,  0,  0, 0],
};
export const WALK_CHAD = {
  // Confident strut — big arm swing, tall steps, chest stays up
  torsoDy:   [0, -1,  0, -1, 0, -1,  0, -1],
  headDy:    [0, -1,  0, -1, 0, -1,  0, -1],
  armFwdDx:  [0,  2,  3,  2, 0, -2, -3, -2],  // wide swing
  armFwdDy:  [0, -1,  0,  1, 0, -1,  0,  1],
  armBackDx: [0, -2, -3, -2, 0,  2,  3,  2],
  armBackDy: [0,  1,  0, -1, 0,  1,  0, -1],
  legLDx:    [0, -2, -3, -2, 0,  0,  0,  0],  // big stride
  legLDy:    [0, -1, -2, -1, 0,  0,  0,  0],
  legRDx:    [0,  0,  0,  0, 0,  2,  3,  2],
  legRDy:    [0,  0,  0,  0, 0, -1, -2, -1],
};
export const WALK_CIVILIAN = {
  torsoDy:   [0,  0, -1,  0, 0,  0, -1,  0],
  headDy:    [0,  0, -1,  0, 0,  0, -1,  0],
  armFwdDx:  [0,  1,  2,  1, 0, -1, -2, -1],
  armFwdDy:  [0,  0,  0,  0, 0,  0,  0,  0],
  armBackDx: [0, -1, -2, -1, 0,  1,  2,  1],
  armBackDy: [0,  0,  0,  0, 0,  0,  0,  0],
  legLDx:    [0, -1, -2, -1, 0,  0,  0,  0],
  legLDy:    [0, -1, -2, -1, 0,  0,  0,  0],
  legRDx:    [0,  0,  0,  0, 0,  1,  2,  1],
  legRDy:    [0,  0,  0,  0, 0, -1, -2, -1],
};

// ATTACK (6 frames): anticipation / windup / strike / impact / recover / settle
// Damage hitframe = frame 2.
export const ATTACK_VIRGIN = {
  // Awkward short-range lunge — book held low
  torsoDx:  [ 0, -1,  1,  2,  1,  0],
  torsoDy:  [ 1,  2,  0,  0,  1,  0],  // crouches then lunges
  headDy:   [ 1,  1, -1,  0,  0,  0],
  armFwdDx: [-1, -2,  3,  4,  2,  0],
  armFwdDy: [ 1,  2,  0,  0,  0,  0],
  armBackDx:[ 1,  1, -1, -1,  0,  0],
  armBackDy:[-1, -1,  1,  1,  0,  0],
  weaponDx: [-2, -3,  4,  5,  3,  0],
  weaponDy: [ 2,  3,  1,  1,  0,  0],
};
export const ATTACK_CHAD = {
  // Sharp jab — arm fully extends forward high, phone flashes
  torsoDx:  [ 0, -1,  2,  2,  1,  0],
  torsoDy:  [ 0, -1, -2, -1,  0,  0],
  headDy:   [ 0,  0, -1, -1,  0,  0],
  armFwdDx: [-3, -4,  5,  6,  3,  0],
  armFwdDy: [-1, -2, -3, -2, -1,  0],  // arm held HIGH
  armBackDx:[ 1,  2, -1, -1,  0,  0],
  armBackDy:[-1, -1,  1,  1,  0,  0],
  weaponDx: [-4, -5,  6,  8,  4,  0],
  weaponDy: [-2, -3, -3, -2, -1,  0],
};
export const ATTACK_CIVILIAN = {
  torsoDx:  [ 0,  0,  1,  1,  0,  0],
  torsoDy:  [ 0, -1,  0,  0,  0,  0],
  headDy:   [ 0,  0,  0,  0,  0,  0],
  armFwdDx: [-2, -3,  4,  5,  2,  0],
  armFwdDy: [ 0,  0,  0,  0,  0,  0],
  armBackDx:[ 0,  0,  0,  0,  0,  0],
  armBackDy:[ 0,  0,  0,  0,  0,  0],
  weaponDx: [-2, -3,  4,  5,  2,  0],
  weaponDy: [ 0,  0,  0,  0,  0,  0],
};

// HURT (2 frames) — flinch
export const HURT_VIRGIN = {
  // Fold forward, clutch belly
  torsoDx:  [-1, -1],
  torsoDy:  [ 2,  1],
  headDy:   [ 2,  1],
  armFwdDx: [-2, -1],  // arms wrap to belly
  armFwdDy: [ 1,  1],
};
export const HURT_CHAD = {
  // Recoil backward, arm up
  torsoDx:  [ -2, -1],
  torsoDy:  [ 0,  0],
  headDy:   [-1, -1],
  armFwdDx: [ -2, -1],
  armFwdDy: [-3, -2],  // arm protects face
};
export const HURT_CIVILIAN = {
  torsoDx:  [-1,  0],
  torsoDy:  [ 1,  0],
  headDy:   [ 1,  0],
  armFwdDx: [-1,  0],
  armFwdDy: [ 0,  0],
};

// DEATH (3 frames) — fall arc, hold on frame 2
export const DEATH_VIRGIN = {
  // Slow forward slump
  torsoDx:  [ 1,  3,  4],
  torsoDy:  [ 2,  5,  8],  // falls forward-down
  headDy:   [ 2,  5,  9],
  rot:      [ 0.2, 0.5, 0.9],  // visual tilt (forward)
};
export const DEATH_CHAD = {
  // Dramatic backward fall
  torsoDx:  [-1, -3, -4],
  torsoDy:  [-1,  3,  8],
  headDy:   [-1,  4,  9],
  rot:      [-0.2, -0.5, -0.9],
};
export const DEATH_CIVILIAN = {
  torsoDx:  [ 0,  2,  3],
  torsoDy:  [ 2,  5,  8],
  headDy:   [ 2,  5,  9],
  rot:      [ 0.1, 0.4, 0.8],
};

export const POSE_CURVES = {
  virgin:      { idle: IDLE_VIRGIN,      walk: WALK_VIRGIN,      attack: ATTACK_VIRGIN,      hurt: HURT_VIRGIN,      death: DEATH_VIRGIN },
  chad: { idle: IDLE_CHAD, walk: WALK_CHAD, attack: ATTACK_CHAD, hurt: HURT_CHAD, death: DEATH_CHAD },
  civilian:   { idle: IDLE_CIVILIAN,   walk: WALK_CIVILIAN,   attack: ATTACK_CIVILIAN,   hurt: HURT_CIVILIAN,   death: DEATH_CIVILIAN },
} as const;
